<template>
  <div class="page-wrap">

    <!-- ── Page Header ── -->
    <div class="page-header">
      <div class="page-header__left">
        <button class="btn-back" @click="$router.back()">
          <i class="fa fa-arrow-left"></i>
        </button>
        <div>
          <h1 class="page-title">Create Promotion Code</h1>
          <span class="page-subtitle">New discount / promo code</span>
        </div>
      </div>
    </div>

    <!-- ── Main Form Panel ── -->
    <div class="panel">
      <div class="panel-title"><i class="fa fa-ticket"></i> Promotion Details</div>

      <div class="form-grid">
        <!-- Code -->
        <div class="field-group">
          <label class="field-label">Code</label>
          <input
            v-model="form.code"
            class="dd-input"
            placeholder="e.g. SUMMER2025"
          />
        </div>

        <!-- Type -->
        <div class="field-group">
          <label class="field-label">Type</label>
          <select v-model="form.type" class="dd-select">
            <option value="" disabled>Select type...</option>
            <option v-for="type in types" :value="type" :key="type">{{ type }}</option>
          </select>
        </div>

        <!-- Value -->
        <div class="field-group">
          <label class="field-label">
            Value
            <span class="field-hint" v-if="form.type === 'Percent'">(1 – 100)</span>
            <span class="field-hint" v-if="form.type === 'Amount'">(AED)</span>
          </label>
          <input
            v-model="form.value"
            class="dd-input"
            :class="{ 'dd-input--error': checkValue }"
            placeholder="promotion value"
          />
          <span class="field-error" v-if="checkValue">Percent value must be between 1 and 100</span>
        </div>

        <!-- Usage Limit -->
        <div class="field-group">
          <label class="field-label">Usage Limit <span class="field-hint">(optional)</span></label>
          <input
            v-model="form.limit_usage"
            class="dd-input"
            placeholder="e.g. 100"
          />
        </div>

        <!-- Start Date -->
        <div class="field-group">
          <label class="field-label">Start Date</label>
          <date-picker
            v-model="form.start_at"
            type="datetime"
            value-type="YYYY-MM-DD HH:mm:ss"
            format="YYYY-MM-DD HH:mm:ss"
            class="dp-full"
          ></date-picker>
        </div>

        <!-- Expire Date -->
        <div class="field-group">
          <label class="field-label">Expiration Date</label>
          <date-picker
            v-model="form.expire_at"
            type="datetime"
            value-type="YYYY-MM-DD HH:mm:ss"
            format="YYYY-MM-DD HH:mm:ss"
            class="dp-full"
          ></date-picker>
        </div>
      </div>
    </div>

    <!-- ── Products Panel ── -->
    <div class="panel">
      <div class="panel-title"><i class="fa fa-cube"></i> Applicable Products <span class="panel-title__hint">(optional — leave empty for all)</span></div>

      <div class="product-search-wrap">
        <div class="input-icon-wrap">
          <i class="fa fa-search input-icon"></i>
          <input
            v-model="search"
            class="dd-input dd-input--icon"
            placeholder="Search product title..."
            @keyup="loadExperiences"
          />
        </div>

        <ul class="search-results" v-if="experiences.length > 0">
          <li
            class="search-result-item"
            v-for="experience of experiences"
            :key="experience.id"
            @click="addExperience(experience.id, experience.title)"
          >
            <i class="fa fa-plus-circle"></i>
            <span>{{ experience.title }}</span>
          </li>
        </ul>
      </div>

      <div class="selected-tags" v-if="form.experiences.length > 0">
        <span
          class="product-tag"
          v-for="experience of form.experiences"
          :key="experience.id"
          @click="removeExperience(experience.id)"
        >
          {{ experience.title }}
          <i class="fa fa-times tag-remove"></i>
        </span>
      </div>
      <div class="empty-products" v-else>
        <i class="fa fa-cube"></i> All products eligible
      </div>
    </div>

    <!-- ── Actions ── -->
    <div class="actions-bar">
      <button class="btn-cancel" @click="$router.back()">Cancel</button>
      <button class="btn-submit" @click="submit()">
        <i class="fa fa-check"></i> Create Promotion
      </button>
    </div>

  </div>
</template>

<script>
import { config } from "../helpers";
import DatePicker from 'vue2-datepicker';
import 'vue2-datepicker/index.css';
import multiselect from 'vue-multiselect'

export default {
  components: {
    DatePicker,
    multiselect
  },

  data() {
    return {
      types: ['Percent', 'Amount'],
      url: "",
      loaded: false,
      search: '',
      experiences: [],
      selectedProducts: [],

      form: {
        code: "",
        type: "",
        value: "",
        start_at: "",
        expire_at: "",
        limit_usage: "",
        experiences: [],
        products: []
      },
      errors: [],
      totalPage: 2,
      page: 1
    };
  },

  mounted() {
    this.url = config("BACKEND_ROOT") + "/admin/promotions";
  },

  computed: {
    checkValue() {
      return this.form.type === 'Percent' && (this.form.value < 1 || this.form.value > 100) && this.form.value !== ''
    }
  },

  watch: {
    errors() {
      this.errors.map(error => {
        this.$notify({
          group: 'notify',
          type: 'warn',
          title: 'Error!',
          text: error
        });
      });
    }
  },

  methods: {
    loadExperiences() {
      const searched = this.search;
      axios
        .get(config("BACKEND_ROOT") + "/admin/products", { params: { 'filters[title]': searched } })
        .then((response) => {
          this.experiences = response.data.data;
          this.experiencesLastPage = response.data.meta.last_page;
        });
    },

    addExperience(id, title) {
      if (this.form.experiences.some((el) => el.id == id)) {
        return alert("You've already added this item!");
      }
      this.form.experiences.push({ id, title });
      this.experiences = [];
      this.search = '';
    },

    removeExperience(id) {
      if (confirm("Do you want to remove this item?")) {
        this.form.experiences = this.form.experiences.filter((el) => el.id != id);
      }
    },

    submit() {
      this.form.products = this.form.experiences.map((product) => product.id);
      axios
        .post(`${this.url}`, this.form)
        .then(() => {
          this.$router.push("/promotions?success=true");
        })
        .catch((error) => {
          this.errors = Object.values(error.response.data.errors).flat();
        });
    },
  },
};
</script>

<style scoped>
.page-wrap { display: flex; flex-direction: column; gap: 16px; }

/* ── Header ── */
.page-header { display: flex; align-items: flex-start; justify-content: space-between; gap: 16px; flex-wrap: wrap; }
.page-header__left { display: flex; align-items: flex-start; gap: 12px; }
.btn-back { width: 34px; height: 34px; margin-top: 4px; background: var(--bg-hover); border: 1px solid var(--border); border-radius: 8px; color: var(--text-2); cursor: pointer; display: flex; align-items: center; justify-content: center; flex-shrink: 0; transition: border-color .15s, color .15s; }
.btn-back:hover { border-color: var(--accent); color: var(--accent); }
.page-title   { font-size: 20px; font-weight: 500; color: var(--text-1); margin: 0 0 2px; }
.page-subtitle { font-size: 12px; color: var(--text-3); }

/* ── Panel ── */
.panel { background: var(--bg-panel); border: 1px solid var(--border); border-radius: 12px; padding: 1.25rem; }
.panel-title { font-size: 12px; font-weight: 600; color: var(--text-3); text-transform: uppercase; letter-spacing: .5px; margin-bottom: 1.25rem; display: flex; align-items: center; gap: 7px; }
.panel-title__hint { font-weight: 400; text-transform: none; letter-spacing: 0; color: var(--text-3); font-size: 11px; }

/* ── Form Grid ── */
.form-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 16px; }
.field-group { display: flex; flex-direction: column; gap: 6px; }
.field-label { font-size: 12px; font-weight: 500; color: var(--text-2); display: flex; align-items: center; gap: 5px; }
.field-hint { font-weight: 400; color: var(--text-3); }
.field-error { font-size: 11px; color: #f42f4b; }

/* ── Inputs ── */
.dd-input { height: 38px; background: var(--bg-base); border: 1px solid var(--border); border-radius: 8px; color: var(--text-1); font-size: 13px; padding: 0 12px; outline: none; width: 100%; transition: border-color .15s; box-sizing: border-box; }
.dd-input:focus { border-color: var(--accent); }
.dd-input::placeholder { color: var(--text-3); }
.dd-input--error { border-color: #f42f4b !important; }
.dd-select { height: 38px; background: var(--bg-base); border: 1px solid var(--border); border-radius: 8px; color: var(--text-1); font-size: 13px; padding: 0 10px; outline: none; width: 100%; transition: border-color .15s; cursor: pointer; }
.dd-select:focus { border-color: var(--accent); }
.dp-full { width: 100%; }
.dp-full >>> .mx-input { height: 38px; background: var(--bg-base); border: 1px solid var(--border); border-radius: 8px; color: var(--text-1); font-size: 13px; box-shadow: none; }
.dp-full >>> .mx-input:focus { border-color: var(--accent); }

/* ── Input with icon ── */
.input-icon-wrap { position: relative; }
.input-icon { position: absolute; left: 10px; top: 50%; transform: translateY(-50%); color: var(--text-3); font-size: 13px; pointer-events: none; }
.dd-input--icon { padding-left: 32px; }

/* ── Product Search ── */
.product-search-wrap { position: relative; max-width: 480px; }
.search-results { list-style: none; padding: 0; margin: 6px 0 0; background: var(--bg-panel); border: 1px solid var(--border); border-radius: 8px; overflow: hidden; }
.search-result-item { display: flex; align-items: center; gap: 8px; padding: 9px 12px; font-size: 13px; color: var(--text-2); cursor: pointer; transition: background .12s; }
.search-result-item:hover { background: var(--bg-hover); color: var(--text-1); }
.search-result-item i { color: var(--accent); font-size: 13px; }

/* ── Selected Tags ── */
.selected-tags { display: flex; flex-wrap: wrap; gap: 8px; margin-top: 1rem; }
.product-tag { display: inline-flex; align-items: center; gap: 6px; padding: 4px 10px; border-radius: 20px; font-size: 12px; font-weight: 500; background: rgba(99,179,237,.12); color: #63b3ed; border: 1px solid rgba(99,179,237,.25); cursor: pointer; transition: background .12s; }
.product-tag:hover { background: rgba(244,47,75,.12); color: #f42f4b; border-color: rgba(244,47,75,.25); }
.tag-remove { font-size: 10px; }
.empty-products { margin-top: 1rem; font-size: 12px; color: var(--text-3); display: flex; align-items: center; gap: 6px; }

/* ── Actions ── */
.actions-bar { display: flex; justify-content: flex-end; gap: 10px; }
.btn-cancel { height: 38px; padding: 0 18px; background: var(--bg-hover); border: 1px solid var(--border); border-radius: 8px; color: var(--text-2); font-size: 13px; cursor: pointer; transition: border-color .15s; }
.btn-cancel:hover { border-color: var(--border-2); color: var(--text-1); }
.btn-submit { height: 38px; padding: 0 20px; background: var(--accent); border: none; border-radius: 8px; color: #fff; font-size: 13px; font-weight: 500; cursor: pointer; display: inline-flex; align-items: center; gap: 7px; transition: opacity .15s; }
.btn-submit:hover { opacity: .88; }

@media (max-width: 768px) { .form-grid { grid-template-columns: 1fr; } }
</style>
